'use client';

import React, { useState, useEffect } from 'react';
import { X } from '@/lib/icons';

interface ModalProps {
  children: React.ReactNode;
  trigger?: React.ReactNode;
  open?: boolean;
  onClose?: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const Modal: React.FC<ModalProps> = ({ 
  children, 
  trigger,
  open: controlledOpen,
  onClose,
  title,
  size = 'md',
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isEnhanced, setIsEnhanced] = useState(false);
  
  // Use controlled state if provided
  const open = controlledOpen !== undefined ? controlledOpen : isOpen;
  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    // Mark as enhanced when JS loads
    setIsEnhanced(true);
    
    // Inject CSS for no-jump behavior
    if (!document.getElementById('modal-base-styles')) {
      const style = document.createElement('style');
      style.id = 'modal-base-styles';
      style.textContent = `
        /* Default state - content inline, not in modal */
        .modal-wrapper[data-enhanced="false"] .modal-trigger {
          display: none;
        }
        
        .modal-wrapper[data-enhanced="false"] .modal-backdrop {
          display: none;
        }
        
        .modal-wrapper[data-enhanced="false"] .modal-container {
          position: static;
          background: transparent;
          box-shadow: none;
          max-width: 100%;
          margin: 1rem 0;
        }
        
        .modal-wrapper[data-enhanced="false"] .modal-close {
          display: none;
        }
        
        /* Enhanced state - full modal functionality */
        .modal-wrapper[data-enhanced="true"] .modal-fallback-content {
          display: none;
        }
        
        .modal-wrapper[data-enhanced="true"] .modal-container {
          display: none;
        }
        
        .modal-wrapper[data-enhanced="true"][data-open="true"] .modal-container {
          display: block;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!isEnhanced) return;
    
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    
    if (open) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [open, isEnhanced]);

  const sizeClasses = {
    sm: 'max-w-sm lg:max-w-md',
    md: 'max-w-md lg:max-w-lg',
    lg: 'max-w-lg lg:max-w-2xl',
    xl: 'max-w-2xl lg:max-w-4xl'
  };

  return (
    <div className="modal-wrapper" data-enhanced={isEnhanced} data-open={open}>
      {/* Trigger - only visible when enhanced */}
      {trigger && (
        <div 
          onClick={() => isEnhanced && setIsOpen(true)} 
          className="modal-trigger inline-block cursor-pointer"
        >
          {trigger}
        </div>
      )}

      {/* Fallback content for non-JS users */}
      {!isEnhanced && (
        <div className="modal-fallback-content">
          <div className="border rounded-md p-4 lg:p-6 my-4 bg-gray-50 mdx-component-base">
            {title && (
              <h3 className="component-title text-gray-900 mb-4" style={{ textWrap: 'balance' }}>
                {title}
              </h3>
            )}
            <div className="prose prose-sm lg:prose-lg max-w-none" style={{ textWrap: 'pretty' }}>
              {children}
            </div>
          </div>
        </div>
      )}

      {/* Modal Content - Always in DOM */}
      <div className="modal-container">
        {/* Backdrop */}
        <div 
          className="modal-backdrop fixed inset-0 z-50 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
          aria-hidden="true"
        />
        
        {/* Modal */}
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 lg:p-4">
            <div 
              className={`
                relative bg-white rounded-md shadow-elevation-3 w-full
                ${sizeClasses[size]} ${className}
              `}
              role="dialog"
              aria-modal="true"
              aria-labelledby={title ? 'modal-title' : undefined}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-4 lg:p-6 border-b">
                <h3 id="modal-title" className="component-title text-gray-900" style={{ textWrap: 'balance' }}>
                  {title || ''}
                </h3>
                <button
                  onClick={handleClose}
                  className="modal-close text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-5 lg:w-6 h-5 lg:h-6" />
                </button>
              </div>
              
              {/* Body */}
              <div className="p-4 lg:p-6 prose prose-sm lg:prose-lg max-w-none" style={{ textWrap: 'pretty' }}>
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};